import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { generateMenuData, VERB_LANDING } from '../../constants/featureNavigation';
import { useSidebarBadges } from '../../hooks/useSidebarBadges';
import { useWebRTC } from '../../context/WebRTCContext';
import { useSocket } from '../../context/SocketContext';
import SidebarUtilityDock from '../../components/SidebarUtilityDock';
import './CompactSidebar.css';

/**
 * CompactSidebar - Single-panel navigation with grouped categories
 *
 * Features:
 * - Collapsible category groups with headers
 * - Indented sub-items under each category
 * - Light theme with warm neutrals
 * - Minimal footprint, maximum clarity
 * - Greys out features that are disabled on the desktop
 */
// Clicking a verb header navigates to its landing page (VERB_LANDING, imported
// from the nav catalog so the sidebar, breadcrumbs, and page titles never
// drift), while the chevron independently collapses its feature list.

const CompactSidebar = ({ currentUser, mobileOpen = false, onNavigate }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [expandedCategories, setExpandedCategories] = useState(() => {
    try {
      const stored = localStorage.getItem('sidebarCollapsed');
      if (stored) return JSON.parse(stored);
    } catch { /* ignore */ }
    return {}; // empty = will default all to expanded
  });
  const [visitedFeatures, setVisitedFeatures] = useState(() => {
    try {
      const stored = localStorage.getItem('visitedFeatures');
      if (stored) return new Set(JSON.parse(stored));
    } catch { /* ignore */ }
    return new Set();
  });
  const { badges } = useSidebarBadges();
  const { remoteCapabilities } = useWebRTC();
  const { selectedSocket } = useSocket();
  const selectedRole = selectedSocket?.role || null;

  // Mark current route's feature as visited
  const markVisited = useCallback((featureId) => {
    setVisitedFeatures(prev => {
      if (prev.has(featureId)) return prev;
      const next = new Set(prev);
      next.add(featureId);
      localStorage.setItem('visitedFeatures', JSON.stringify([...next]));
      return next;
    });
  }, []);

  // Rebuild menu whenever remote capabilities change (includes enabled/muted flags)
  const menuItems = useMemo(
    () => generateMenuData(remoteCapabilities, selectedRole),
    [remoteCapabilities, selectedRole]
  );

  // Fill in any new categories as expanded (first-use or newly added categories)
  useEffect(() => {
    setExpandedCategories(prev => {
      let changed = false;
      const next = { ...prev };
      menuItems.forEach(item => {
        if (!(item.id in next)) {
          next[item.id] = true;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [menuItems]);

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => {
      const next = { ...prev, [categoryId]: !prev[categoryId] };
      localStorage.setItem('sidebarCollapsed', JSON.stringify(next));
      return next;
    });
  };

  // Ensure a category is expanded (used when navigating to its verb landing so
  // the feature list opens rather than staying collapsed).
  const expandCategory = (categoryId) => {
    setExpandedCategories(prev => {
      if (prev[categoryId]) return prev;
      const next = { ...prev, [categoryId]: true };
      localStorage.setItem('sidebarCollapsed', JSON.stringify(next));
      return next;
    });
  };

  const isActiveRoute = (path) => {
    return location.pathname.startsWith(path);
  };

  // Mark active feature as visited on route change
  useEffect(() => {
    for (const category of menuItems) {
      const activeSub = category.subs?.find(sub => isActiveRoute(sub.to));
      if (activeSub && !activeSub.disabled) {
        markVisited(activeSub.id);
        break;
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Close mobile sidebar on navigation — delegates to the parent's state.
  const closeMobileSidebar = () => { onNavigate?.(); };

  const filteredList = (items) => {
    const role = currentUser?.role;
    return items.filter(x => (x.roles && x.roles.includes(role)) || !x.roles);
  };

  // Get category icon based on ID for visual grouping
  const getCategoryIcon = (categoryId) => {
    const icons = {
      watch: 'simple-icon-eye',
      question: 'simple-icon-bubbles',
      back: 'simple-icon-clock',
      do: 'iconsminds-flash-2',
      setup: 'simple-icon-settings',
    };
    return icons[categoryId] || 'simple-icon-folder';
  };

  return (
    <aside id="compact-sidebar" className={classnames('compact-sidebar', { 'mobile-open': mobileOpen })}>
      <NavLink to="/app/home" className="sidebar-brand" onClick={closeMobileSidebar}>
        <div className="brand-icon">
          <i className="iconsminds-security-camera" />
        </div>
        <span className="brand-text">Inquisitor</span>
      </NavLink>

      <nav className="sidebar-nav">
        {filteredList(menuItems).map((category) => {
          const isExpanded = expandedCategories[category.id];
          const hasActiveSub = category.subs?.some(sub => isActiveRoute(sub.to));

          return (
            <div
              key={category.id}
              data-category={category.id}
              className={classnames('nav-category', {
                'is-expanded': isExpanded,
                'has-active': hasActiveSub
              })}
            >
              {/* Category header — clicking the verb navigates to its landing
                  page and opens the list; the chevron only collapses/expands. */}
              <button
                className="category-header"
                onClick={() => {
                  const landing = VERB_LANDING[category.id];
                  if (landing) {
                    navigate(landing);
                    expandCategory(category.id);
                    closeMobileSidebar();
                  } else {
                    toggleCategory(category.id);
                  }
                }}
                data-tooltip={category.description || category.label}
              >
                <div className="category-header-content">
                  <i className={getCategoryIcon(category.id)} />
                  <span className="category-label">{category.label}</span>
                </div>
                <span
                  className="chevron-toggle"
                  role="button"
                  tabIndex={0}
                  aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${category.label}`}
                  aria-expanded={isExpanded}
                  onClick={(e) => { e.stopPropagation(); toggleCategory(category.id); }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault(); e.stopPropagation(); toggleCategory(category.id);
                    }
                  }}
                >
                  <i className={classnames('chevron simple-icon-arrow-down', {
                    'rotated': !isExpanded
                  })} />
                </span>
              </button>

              {/* Sub Items */}
              <div className={classnames('category-items', {
                'collapsed': !isExpanded
              })}>
                {category.subs && filteredList(category.subs).map((sub, index, arr) => {
                  const isActive = isActiveRoute(sub.to);
                  const badge = badges[sub.id];
                  const prevGroup = index > 0 ? arr[index - 1].group : null;
                  const showDivider = sub.group && sub.group !== prevGroup;

                  return (
                    <React.Fragment key={sub.id}>
                      {showDivider && (
                        <div className="nav-group-divider">{sub.group}</div>
                      )}
                      <NavLink
                        to={sub.disabled ? '#' : sub.to}
                        className={classnames('nav-item', {
                          'active': isActive,
                          'feature-disabled': sub.disabled,
                          'feature-muted': sub.muted && !sub.disabled,
                          'nav-item-has-activity': badge && badge.count > 0 && !sub.disabled,
                        })}
                        data-tooltip={
                          sub.disabled ? `${sub.label} (turned off on the desktop)`
                          : sub.muted ? `${sub.label} (paused — not receiving updates)`
                          : sub.description || sub.label
                        }
                        onClick={(e) => {
                          if (sub.disabled) { e.preventDefault(); return; }
                          markVisited(sub.id);
                          closeMobileSidebar();
                        }}
                      >
                        <i className={sub.icon} />
                        <span className="nav-item-label">{sub.label}</span>
                        {sub.disabled && (
                          <span className="nav-item-disabled-badge">OFF</span>
                        )}
                        {sub.muted && !sub.disabled && (
                          <span className="nav-item-muted-badge">PAUSED</span>
                        )}
                        {badge && !isActive && !sub.disabled && (
                          <span className={classnames('nav-item-badge', {
                            'badge-high': badge.priority === 'high'
                          })}>
                            {badge.count > 9 ? '9+' : badge.count}
                          </span>
                        )}
                        {badge && badge.count > 0 && !isActive && !sub.disabled && (
                          <span className="nav-item-active-dot" />
                        )}
                        {isActive && !sub.disabled && <div className="active-indicator" />}
                      </NavLink>
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>

      <SidebarUtilityDock />
    </aside>
  );
};

const mapStateToProps = ({ authUser }) => {
  const { currentUser } = authUser;
  return { currentUser };
};

export default connect(mapStateToProps)(CompactSidebar);
